/* eslint-disable @typescript-eslint/no-explicit-any */
import { action, makeObservable, observable, reaction, when } from 'mobx';
import WCSignClient, { SignClient } from '@walletconnect/sign-client';
import { flowLoading, toGenerator } from '../../utils';
import UniversalProvider, { ConnectParams, Namespace } from '@walletconnect/universal-provider';
import { SessionTypes } from '@walletconnect/types';
import { DEFAULT_LOGGER, WALLETCONNET_PROJECT_ID, WalletConnectRPCMethods, WalletConnectChainID } from './constants';
import type ExplorerRootStore from '../../ExplorerRootStore';
import { WalletConnectionError } from '@solana/wallet-adapter-base';
import WCWalletList from './WalletList';

export const EVM_NS = 'eip155' as const;
export const SOL_NS = 'solana' as const;
export type SupportedWCNS = typeof EVM_NS | typeof SOL_NS;

export interface SessionData {
	session: SessionTypes.Struct;
	client: WCSignClient;
	universalProvider: UniversalProvider;
}

function sessionSupportsSolana(session: SessionTypes.Struct): boolean {
	if (!session.namespaces[SOL_NS]?.chains?.length) {
		return false;
	}
	return session.namespaces[SOL_NS].chains.includes(WalletConnectChainID.Mainnet);
}

function sessionSupportsEVM(session: SessionTypes.Struct): boolean {
	return Boolean(session.namespaces[EVM_NS]);
}

function sortBy<T>(arr: T[], fn: (item: T) => number): T[] {
	return [...arr].sort((a, b) => fn(a) - fn(b));
}

export default class WalletConnectClient {
	#root: ExplorerRootStore;
	evmProvider: UniversalProvider | undefined = undefined;
	solProvider: UniversalProvider | undefined = undefined;
	displayUri: string | undefined = undefined;

	#client: WCSignClient | undefined;
	initializing = true;
	walletList: WCWalletList = new WCWalletList(this);

	constructor(root: ExplorerRootStore) {
		this.#root = root;
		makeObservable(this, {
			evmProvider: observable.ref,
			solProvider: observable.ref,
			displayUri: observable,
			initializing: observable,
			walletList: false,
		});
		this.getClient();
		reaction(
			() => this.evmProvider,
			(next, prev) => {
				this.#removeProviderListeners(prev);
				this.#addProviderListeners(next);
			},
		);
		reaction(
			() => this.solProvider,
			(next, prev) => {
				this.#removeProviderListeners(prev);
				this.#addProviderListeners(next);
			},
		);
	}

	get evmChains() {
		return this.#root.configs.transformedChains.filter((chain) => chain.mode === 'EVM' && chain.nameId !== 'hypercore');
	}

	get #rpcMap() {
		return this.evmChains.reduce((acc, chain) => {
			acc[chain.chainId] = chain.externalRpcURL || chain.rpcURL;
			return acc;
		}, {} as Record<number, string>);
	}

	async waitForReady(timeout = 1_000) {
		if (this.initializing) {
			let gettingClient = false;
			try {
				const promises = [];
				promises.push(when(() => !this.initializing, { timeout }));

				if (this.#client) {
					gettingClient = true;
					promises.push(this.getClient());
				}
				await Promise.race(promises);
			} catch {
				if (gettingClient) {
					this.getClient.cancel();
				}
				throw new Error('Walletconnect initialization took too long');
			}
		}
	}

	getClient = flowLoading(function* (this: WalletConnectClient) {
		if (!this.#client) {
			this.#client = yield* toGenerator(
				SignClient.init({
					projectId: WALLETCONNET_PROJECT_ID,
					logger: DEFAULT_LOGGER,
				}),
			);
			try {
				const sessions = sortBy(this.#client.session.getAll(), (session) => {
					let counter = 0;
					if (sessionSupportsEVM(session)) counter--;
					if (sessionSupportsSolana(session)) counter--;
					return counter;
				});
				for (const session of sessions) {
					yield this.#restoreSession(session);
				}
				yield;
			} catch (e) {
				console.warn('WalletConnect restore sessions error', e);
			}
		}
		if (this.#client.core.relayer.connecting) {
			yield this.#client.core.relayer.transportOpen();
		}
		this.initializing = false;
		return this.#client;
	});

	async #restoreSession(session: SessionTypes.Struct) {
		if (this.evmProvider && this.solProvider) return;
		const provider = await UniversalProvider.init({
			client: this.#client,
			session: session,
		});
		this.#setSession(provider, session);
	}

	connectEvm = flowLoading(function* (this: WalletConnectClient) {
		yield this.waitForReady(5_000);
		if (!this.#client) throw new Error('Signer not initialized');
		if (this.evmProvider?.session) return;
		this.displayUri = undefined;
		const universalProvider = yield* toGenerator(
			UniversalProvider.init({
				client: this.#client,
			}),
		);
		let session;
		try {
			universalProvider.on('display_uri', this.#handleDisplayUri);
			session = yield* toGenerator(this.#connectEvmSession(universalProvider));
		} finally {
			universalProvider.removeListener('display_uri', this.#handleDisplayUri);
		}
		if (!session) {
			throw new WalletConnectionError();
		}
		this.#setSession(universalProvider, session);
	});

	connectSolana = flowLoading(function* (this: WalletConnectClient) {
		yield this.waitForReady(5_000);
		if (!this.#client) throw new Error('Signer not initialized');
		if (this.solProvider?.session && sessionSupportsSolana(this.solProvider.session)) return;
		const universalProvider = yield* toGenerator(
			UniversalProvider.init({
				client: this.#client,
			}),
		);
		let session;
		try {
			universalProvider.on('display_uri', this.#handleDisplayUri);
			session = yield* toGenerator(this.#connectSolSession(universalProvider));
		} finally {
			universalProvider.removeListener('display_uri', this.#handleDisplayUri);
		}
		if (!session) {
			throw new WalletConnectionError();
		}
		this.#setSession(universalProvider, session);
	});

	disconnectEvm = () => {
		return this.#removeSession(this.evmProvider);
	};

	disconnectSolana = () => {
		return this.#removeSession(this.solProvider);
	};

	#removeSession = flowLoading(function* (this: WalletConnectClient, provider: UniversalProvider | undefined) {
		if (!provider) return;
		const topic = provider.session?.topic;
		const removeSol = this.solProvider?.session === undefined || this.solProvider?.session.topic === topic;
		const removeEvm = this.evmProvider?.session === undefined || this.evmProvider?.session.topic === topic;
		yield provider.disconnect();
		if (removeSol) this.solProvider = undefined;
		if (removeEvm) this.evmProvider = undefined;
	});

	#setSession = action((universalProvider: UniversalProvider, session: SessionTypes.Struct) => {
		if (sessionSupportsEVM(session) && !this.evmProvider) {
			this.evmProvider = universalProvider;
		}
		if (sessionSupportsSolana(session) && !this.solProvider) {
			this.solProvider = universalProvider;
		}
	});

	#connectEvmSession(universalProvider: UniversalProvider) {
		const config: ConnectParams = {
			namespaces: {
				[EVM_NS]: this.#evmConfig(),
			},
		};
		if (!this.solProvider) {
			config.optionalNamespaces = { [SOL_NS]: this.#solConfig() };
		}
		return universalProvider.connect(config);
	}

	#connectSolSession(universalProvider: UniversalProvider) {
		const config: ConnectParams = {
			namespaces: {
				[SOL_NS]: this.#solConfig(),
			},
		};
		if (!this.evmProvider) {
			config.optionalNamespaces = { [EVM_NS]: this.#evmConfig() };
		}
		return universalProvider.connect(config);
	}

	#evmConfig(): Namespace {
		const evmChains = this.evmChains.map((c) => `${EVM_NS}:${c.chainId}`);
		return {
			methods: [
				'eth_sendTransaction',
				'eth_signTransaction',
				'eth_sign',
				'personal_sign',
				'eth_signTypedData',
				'eth_requestAccounts',
				'eth_signTypedData_v4',
				'eth_signTypedData_v3',
				'wallet_switchEthereumChain',
				'wallet_addEthereumChain',
			],
			chains: evmChains,
			events: ['chainChanged', 'accountsChanged', 'disconnect', 'connect'],
			rpcMap: this.#rpcMap,
		};
	}

	#solConfig(): Namespace {
		return {
			chains: [WalletConnectChainID.Mainnet],
			methods: [WalletConnectRPCMethods.signTransaction, WalletConnectRPCMethods.signMessage],
			events: [],
		};
	}

	#handleDisplayUri = action((uri: string) => {
		this.displayUri = uri;
	});

	#handleSessionPing = action((args: any) => {
		console.log('EVENT', 'session_ping', args);
	});

	#handleSessionEvent = action((args: any) => {
		console.log('EVENT', 'session_event', args);
	});

	#handleSessinUpdate = action(({ topic, session }: { topic: string; session: SessionTypes.Struct }) => {
		console.log('EVENT', 'session_updated', topic, session);
	});

	#handleSessionDelete = action((args: any) => {
		const { topic }: { id: number; topic: string } = args;
		console.log('EVENT', 'session_deleted', args);
		if (this.solProvider?.session?.topic === topic) this.solProvider = undefined;
		if (this.evmProvider?.session?.topic === topic) this.evmProvider = undefined;
	});

	#addProviderListeners(provider: UniversalProvider | undefined) {
		if (!provider) return;
		provider.on('display_uri', this.#handleDisplayUri);
		provider.on('session_ping', this.#handleSessionPing);
		provider.on('session_event', this.#handleSessionEvent);
		provider.on('session_update', this.#handleSessinUpdate);
		provider.on('session_delete', this.#handleSessionDelete);
	}
	#removeProviderListeners(provider: UniversalProvider | undefined) {
		if (!provider) return;
		provider.removeListener('display_uri', this.#handleDisplayUri);
		provider.removeListener('session_ping', this.#handleSessionPing);
		provider.removeListener('session_event', this.#handleSessionEvent);
		provider.removeListener('session_update', this.#handleSessinUpdate);
		provider.removeListener('session_delete', this.#handleSessionDelete);
		if (provider.session) {
			provider.disconnect();
		}
	}
}
